'use client'

import Select from "@/components/elements/Select"

/**
 * Tone and length refinements for the AI suggester.
 *
 * Two dropdowns with no visible label: the placeholder names the control until
 * something is chosen. That leaves the trigger unnamed once it holds a value,
 * so each carries an aria-label - the one reader who cannot infer a control
 * from its options is the one who cannot see the two side by side.
 *
 * Neither is required. `any` maps back to null, which is what an untouched form
 * sends, and the model keeps its own spread of naming strategies.
 *
 * The ids are the wire format. They are matched against a fixed table on the
 * server, so a value invented here reaches no prompt.
 */

/** Radix reserves the empty string for clearing, so "no preference" needs a
 *  real value of its own. It never leaves this file. */
const ANY = 'any'

export const TONES = [
  { id: 'professional', label: 'Мэргэжлийн' },
  { id: 'friendly', label: 'Найрсаг' },
  { id: 'playful', label: 'Загварлаг' },
  { id: 'mongolian', label: 'Монгол' },
] as const

export const LENGTHS = [
  { id: 'short', label: 'Богино', desc: '3-6' },
  { id: 'mid', label: 'Дунд', desc: '7-9' },
  { id: 'long', label: 'Урт', desc: '10-12' },
] as const

export type ToneId = typeof TONES[number]['id']
export type LengthId = typeof LENGTHS[number]['id']

const TONE_ITEMS = [
  { value: ANY, label: 'Авто' },
  ...TONES.map(t => ({ value: t.id, label: t.label })),
]

const LENGTH_ITEMS = [
  { value: ANY, label: 'Урт хамаагүй' },
  ...LENGTHS.map(l => ({ value: l.id, label: l.label, desc: l.desc })),
]

export default function AiOptions({ tone, length, onTone, onLength }: {
  tone: ToneId | null
  length: LengthId | null
  onTone: (next: ToneId | null) => void
  onLength: (next: LengthId | null) => void
}) {
  return (
    <div className="flex items-center gap-2 w-full sm:w-auto">
      {/* Sized by a wrapper rather than through the Select's own className: the
          component sets w-full internally, and two width utilities in one class
          list are settled by stylesheet order, not by the order written.

          Fluid below sm because two fixed 9rem triggers overflow a 320px phone
          once the panel's padding is taken out; fixed from sm up so they do not
          stretch across a wide panel. */}
      <div className="flex-1 min-w-0 sm:flex-none sm:w-40">
        <Select
          aria-label="Нэрний өнгө аяс"
          value={tone ?? undefined}
          onChange={(v) => onTone(v === ANY ? null : v as ToneId)}
          items={TONE_ITEMS}
          placeholder="Өнгө аяс"
        />
      </div>
      <div className="flex-1 min-w-0 sm:flex-none sm:w-36">
        <Select
          aria-label="Нэрний урт"
          value={length ?? undefined}
          onChange={(v) => onLength(v === ANY ? null : v as LengthId)}
          items={LENGTH_ITEMS}
          placeholder="Урт"
        />
      </div>
    </div>
  )
}
